import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { MapPin } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { TextField } from "../shared/FormField";
import { AddressAutocomplete } from "../customer/AddressAutocomplete";
import { LocationPicker } from "./LocationPicker";
import { apiClient, getApiErrorMessage, getFieldErrors } from "../../lib/apiClient";
import { putWithVersion, isVersionConflict, conflictMessage, conflictLatest } from "../../lib/conflict";

const EMPTY_FORM = {
  firstname: "", lastname: "", email: "", phone: "",
  house_no: "", address: "", city: "", state: "", postal_code: "",
  latitude: "", longitude: "", geo_source: null,
};

const hasCoords = (lat, lng) => {
  const la = parseFloat(lat), ln = parseFloat(lng);
  return Number.isFinite(la) && Number.isFinite(ln) && !(la === 0 && ln === 0);
};

export function CustomerFormDialog({ open, onOpenChange, onSaved, customer }) {
  const isEdit = Boolean(customer);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(null); // { message, latest }
  const versionRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    versionRef.current = customer?.updated_at || null;
    setConflict(null);
    setForm(
      customer
        ? {
            firstname: customer.firstname || "", lastname: customer.lastname || "",
            email: customer.email || "", phone: customer.phone || "",
            house_no: customer.house_no || "", address: customer.address || "",
            city: customer.city || "", state: customer.state || "", postal_code: customer.postal_code || "",
            latitude: customer.latitude || "", longitude: customer.longitude || "",
            geo_source: customer.geo_source || null,
          }
        : EMPTY_FORM
    );
    setErrors({});
  }, [open, customer]);

  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleAddressSelect = (r) => {
    setForm((f) => ({
      ...f,
      address: r.address || f.address,
      city: r.city || f.city, state: r.state || f.state, postal_code: r.postal_code || f.postal_code,
      latitude: r.latitude || f.latitude, longitude: r.longitude || f.longitude,
      geo_source: "auto",
    }));
  };

  // Admin dragging the pin or editing the lat/long text = an explicit manual override.
  const handlePinChange = (lat, lng) =>
    setForm((f) => ({ ...f, latitude: String(lat), longitude: String(lng), geo_source: "manual" }));
  const setCoordField = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value, geo_source: "manual" }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});
    const payload = {
      ...form,
      latitude: form.latitude || null,
      longitude: form.longitude || null,
    };
    try {
      if (isEdit) {
        await putWithVersion(`/customers/${customer.id}`, payload, versionRef.current);
        toast.success("Customer updated");
        onSaved();
      } else {
        const { data } = await apiClient.post("/customers", payload);
        toast.success("Customer created");
        onSaved(data);
      }
    } catch (error) {
      if (isVersionConflict(error)) {
        const latest = conflictLatest(error);
        // Keep the admin's unsaved edits; surface the latest server data and
        // advance the version token so a deliberate re-save will go through.
        if (latest?.updated_at) versionRef.current = latest.updated_at;
        setConflict({ message: conflictMessage(error), latest });
        toast.warning(conflictMessage(error));
      } else {
        setErrors(getFieldErrors(error));
        toast.error(getApiErrorMessage(error, "Could not save customer."));
      }
    } finally {
      setSaving(false);
    }
  };

  const coordsSet = hasCoords(form.latitude, form.longitude);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="customer-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Customer" : "New Customer"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update this customer's contact and address details."
              : "Core contact and address details. A login account is created automatically."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {conflict && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm" data-testid="customer-conflict-banner">
              <p className="font-medium text-amber-900">{conflict.message}</p>
              {conflict.latest && (
                <p className="mt-1 text-xs text-amber-800">
                  Latest on server — {conflict.latest.firstname} {conflict.latest.lastname}
                  {conflict.latest.phone ? ` · ${conflict.latest.phone}` : ""}
                  {conflict.latest.address ? ` · ${conflict.latest.address}` : ""}.
                  Your edits are kept — press Save again to apply them over the latest.
                </p>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <TextField label="First Name" required value={form.firstname} onChange={setField("firstname")} error={errors.firstname} testId="customer-form-firstname" />
            <TextField label="Last Name" required value={form.lastname} onChange={setField("lastname")} error={errors.lastname} testId="customer-form-lastname" />
            <TextField label="Email" required type="email" value={form.email} onChange={setField("email")} error={errors.email} testId="customer-form-email" />
            <TextField label="Phone" required value={form.phone} onChange={setField("phone")} error={errors.phone} testId="customer-form-phone" />
          </div>

          <AddressAutocomplete
            value={form.address}
            onChange={(v) => setForm((f) => ({ ...f, address: v }))}
            onSelect={handleAddressSelect}
            label="Address"
            id="customer-form-address-autocomplete"
            testIdPrefix="customer-form-address"
          />
          {errors.address && <p className="-mt-2 text-xs text-danger" data-testid="customer-form-address-error">{errors.address}</p>}

          <div className="space-y-1.5">
            <Label htmlFor="customer-form-house-no">House / Unit No. (optional)</Label>
            <Input id="customer-form-house-no" data-testid="customer-form-house-no" value={form.house_no} onChange={setField("house_no")} placeholder="e.g. Unit 204" />
            {errors.house_no && <p className="text-xs text-danger">{errors.house_no}</p>}
          </div>

          <div className="grid grid-cols-3 gap-4">
            <TextField label="City" required value={form.city} onChange={setField("city")} error={errors.city} testId="customer-form-city" />
            <TextField label="State" required value={form.state} onChange={setField("state")} error={errors.state} testId="customer-form-state" />
            <TextField label="Postal Code" required value={form.postal_code} onChange={setField("postal_code")} error={errors.postal_code} testId="customer-form-postal-code" />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Location on map</Label>
              {form.geo_source === "manual" && (
                <span className="text-[11px] font-medium text-accent" data-testid="customer-form-geo-manual-badge">Pin set manually</span>
              )}
            </div>
            <p className="text-xs text-text-muted" data-testid="customer-form-location-help">
              Drag the pin or tap the map to fine-tune. Leave blank and we'll look it up from the address automatically on save.
            </p>
            <LocationPicker lat={form.latitude} lng={form.longitude} onChange={handlePinChange} editable height={220} />
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="customer-form-latitude">Latitude</Label>
                <Input id="customer-form-latitude" data-testid="customer-form-latitude" value={form.latitude} onChange={setCoordField("latitude")} placeholder="e.g. 53.5461" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="customer-form-longitude">Longitude</Label>
                <Input id="customer-form-longitude" data-testid="customer-form-longitude" value={form.longitude} onChange={setCoordField("longitude")} placeholder="e.g. -113.4938" />
              </div>
            </div>
            {!coordsSet && (
              <p className="flex items-center gap-1.5 text-xs text-warning" data-testid="customer-form-address-hint">
                <MapPin size={13} strokeWidth={1.5} /> No coordinates yet — we'll geocode the address automatically when you save.
              </p>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)} data-testid="customer-form-cancel-button">
              Cancel
            </Button>
            <Button type="submit" variant="accent" disabled={saving} data-testid="customer-form-submit-button">
              {saving ? "Saving..." : isEdit ? "Save Changes" : "Create Customer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
